// Bildirim zili veri katmanı: polling'li tek feed sorgusu (rozet + liste) +
// iyimser mark-read / mark-all mutation'ları.
//
// Tek sorgu hem rozeti (unreadCount) hem dropdown listesini besler — dropdown
// kapalıyken de polling ile rozet güncel kalır (K-4: 45-60s poll, SignalR yok).
// İyimser güncelleme cache'i saf `feedState` reducer'larıyla türetir; hata olursa
// snapshot'a geri alınır ve her durumda invalidate ile sunucu gerçeği çekilir.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '@api/notifications';
import { markAllRead, markOneRead } from './feedState';
import type { NotificationFeed } from './types';

/** Tüm bildirim sorgularının kökü — invalidate bunu hedefler. */
export const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;
const FEED_QUERY_KEY = ['notifications', 'feed'] as const;

/** Rozet güncelliği için poll aralığı (K-4: 45-60s). Sekme arka plandayken duraklar. */
const POLL_INTERVAL_MS = 45_000;
/** Dropdown'da gösterilecek son bildirim sayısı (ilk sayfa). */
const FEED_PAGE_SIZE = 20;

export function useNotificationFeed() {
  return useQuery({
    queryKey: FEED_QUERY_KEY,
    queryFn: () => notificationsApi.list({ page: 1, pageSize: FEED_PAGE_SIZE }),
    refetchInterval: POLL_INTERVAL_MS,
    // Rozet çok bayat kalmasın: dropdown açılışı / yeniden odak taze veri çeksin.
    staleTime: 15_000,
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: FEED_QUERY_KEY });
      const previous = qc.getQueryData<NotificationFeed>(FEED_QUERY_KEY);
      if (previous) {
        qc.setQueryData<NotificationFeed>(FEED_QUERY_KEY, markOneRead(previous, id));
      }
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData(FEED_QUERY_KEY, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: FEED_QUERY_KEY });
      const previous = qc.getQueryData<NotificationFeed>(FEED_QUERY_KEY);
      if (previous) {
        qc.setQueryData<NotificationFeed>(FEED_QUERY_KEY, markAllRead(previous));
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(FEED_QUERY_KEY, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY }),
  });
}
